import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as DotsIcon } from '../../icons/dots.svg';
import './ActionMenu.css';

function ActionMenu({ items, align, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ vertical: 'down', horizontal: align });
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updatePosition = () => {
      const buttonEl = buttonRef.current;
      const listEl = listRef.current;
      if (!buttonEl || !listEl) {
        return;
      }

      const buttonRect = buttonEl.getBoundingClientRect();
      const listRect = listEl.getBoundingClientRect();

      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const vertical = spaceBelow < listRect.height + 12 && spaceAbove >= listRect.height + 12 ? 'up' : 'down';

      const spaceRight = window.innerWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;
      let horizontal = align;
      if (horizontal === 'right' && spaceRight < listRect.width + 12 && spaceLeft >= listRect.width + 12) {
        horizontal = 'left';
      } else if (horizontal === 'left' && spaceLeft < listRect.width + 12 && spaceRight >= listRect.width + 12) {
        horizontal = 'right';
      }

      const gap = 8;
      const top = vertical === 'down'
        ? buttonRect.bottom + gap
        : buttonRect.top - listRect.height - gap;

      const left = horizontal === 'right'
        ? buttonRect.right - listRect.width
        : buttonRect.left;

      const clampedTop = Math.max(8, Math.min(top, window.innerHeight - listRect.height - 8));
      const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - listRect.width - 8));

      setPosition({ vertical, horizontal });
      setMenuStyle({
        top: `${clampedTop - 100}px`,
        left: `${70 + clampedLeft}px`,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, align]);

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleItemClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    if (item.disabled) {
      return;
    }
    item.onClick?.();
    setIsOpen(false);
  };

  return (
    <div className="action-menu" ref={menuRef}>
      <button
        type="button"
        className="action-menu-button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={handleToggle}
        ref={buttonRef}
      >
        <DotsIcon />
      </button>
      {isOpen && (
        <div
          ref={listRef}
          className={`action-menu-list action-menu-list-${position.horizontal} action-menu-list-${position.vertical}`}
          style={menuStyle}
          role="menu"
        >
          {items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <button
                key={item.key || item.label}
                type="button"
                role="menuitem"
                className={`action-menu-item${item.danger ? ' danger' : ''}${item.disabled ? ' disabled' : ''}`}
                onClick={(event) => handleItemClick(event, item)}
                disabled={item.disabled}
              >
                {ItemIcon && <ItemIcon />}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

ActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      onClick: PropTypes.func,
      danger: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  align: PropTypes.oneOf(['left', 'right']),
  ariaLabel: PropTypes.string,
};

ActionMenu.defaultProps = {
  align: 'right',
  ariaLabel: 'Otwórz menu akcji',
};

export default ActionMenu;
